import { useNavigate } from 'react-router';
import { CalendarClock, Plus } from 'lucide-react';
import { FundSummary } from '@/types/fund';
import { ShortcutIndicator } from '@/components/keyboard/ShortcutIndicator';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useMetricsReleaseQueue } from '@/hooks/react-query/useFunds';

interface FundsHeaderProps {
  funds: FundSummary[];
  expandedFunds: Set<string>;
  onExpandAll: () => void;
  onCollapseAll: () => void;
}

export function FundsHeader({
  funds,
  expandedFunds,
  onExpandAll,
  onCollapseAll,
}: FundsHeaderProps) {
  const navigate = useNavigate();
  const { data: releaseQueue = [] } = useMetricsReleaseQueue();

  const mainFunds = funds.filter((fund) => !fund.parent_fund_id);

  // Calculate expand/collapse shortcuts availability
  const fundsWithChildren = mainFunds.filter((fund) =>
    funds.some((f) => f.parent_fund_id === fund.id)
  );
  const hasCollapsibleFunds = fundsWithChildren.some((fund) =>
    expandedFunds.has(fund.id)
  );
  const hasExpandableFunds = fundsWithChildren.some(
    (fund) => !expandedFunds.has(fund.id)
  );

  // Keyboard shortcuts for expand/collapse
  useKeyboardShortcuts('funds-page', [
    {
      key: 'e',
      description: 'Expand all funds',
      action: onExpandAll,
      category: 'Fund Management',
    },
    {
      key: 'c',
      description: 'Collapse all funds',
      action: onCollapseAll,
      category: 'Fund Management',
    },
  ]);

  return (
    <div className="flex justify-between items-start">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Funds</h1>
        <p className="text-muted-foreground mt-1">
          Manage your fund portfolio and structures.
        </p>
        {fundsWithChildren.length > 0 && (
          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
            <span>Hierarchy controls:</span>
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center gap-1 ${!hasExpandableFunds ? 'opacity-50' : ''}`}
              >
                <ShortcutIndicator shortcut={{ key: 'e' }} variant="compact" />
                <span>expand all</span>
              </div>
              <div
                className={`flex items-center gap-1 ${!hasCollapsibleFunds ? 'opacity-50' : ''}`}
              >
                <ShortcutIndicator shortcut={{ key: 'c' }} variant="compact" />
                <span>collapse all</span>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-3">
        <button
          className="flex items-center gap-2 px-4 py-2 border border-border rounded-md hover:bg-accent transition-colors"
          onClick={() => navigate('/app/funds/metrics-release')}
        >
          <CalendarClock className="h-4 w-4" />
          Ready to release
          {releaseQueue.length > 0 && (
            <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand px-1.5 text-xs text-brand-foreground">
              {releaseQueue.length}
            </span>
          )}
        </button>
        <button
          className="flex items-center gap-2 px-4 py-2 bg-brand text-brand-foreground rounded-md hover:bg-brand/90 transition-colors"
          onClick={() => navigate('/app/funds/new')}
        >
          <Plus className="h-4 w-4" />
          Create New Fund
        </button>
      </div>
    </div>
  );
}
